/**
 * Loaded data shared with the non-map UI (stats, filter counts).
 *
 * The map renders straight from GeoJSON; the header stats and the per-satellite
 * filter counts need the same detections as plain records they can count in JS.
 * MapView, which owns loading, publishes both these records and the live map
 * instance here so the stats can query the current viewport.
 */
import type { Map as MlMap } from "maplibre-gl";
import { create } from "zustand";

import type { PlumeGrade, Satellite } from "../data/types";
import type { Meta } from "../services/api";

export interface DetectionRecord {
  id: string;
  /** Numeric feature id in the plume source, for setFeatureState. */
  fid: number;
  episodeId: string;
  date: string;
  /** Parsed acquisition time (ms), for age math on the timeline. */
  dateMs: number;
  emissionRate: number | null;
  satellite: Satellite;
  grade: PlumeGrade;
}

export type LoadStatus = "loading" | "ready" | "error";

interface DataState {
  meta: Meta | null;
  records: DetectionRecord[];
  status: LoadStatus;
  /** Live map, for viewport-scoped stats (queryRenderedFeatures). */
  map: MlMap | null;
  /** Bumped on every map moveend so viewport-scoped stats recompute. */
  viewportVersion: number;
  setMeta: (meta: Meta) => void;
  setRecords: (records: DetectionRecord[]) => void;
  setStatus: (status: LoadStatus) => void;
  setMap: (map: MlMap | null) => void;
  bumpViewport: () => void;
}

export const useDataStore = create<DataState>((set) => ({
  meta: null,
  records: [],
  status: "loading",
  map: null,
  viewportVersion: 0,
  setMeta: (meta) => set({ meta }),
  setRecords: (records) => set({ records }),
  setStatus: (status) => set({ status }),
  setMap: (map) => set({ map }),
  bumpViewport: () => set((s) => ({ viewportVersion: s.viewportVersion + 1 })),
}));
